import React from "react";
import { Link } from "react-router-dom";
import { Leaf, Heart, ShieldCheck, Sparkles } from "lucide-react";
import { useData } from "@/context/DataContext";
import Seo from "@/components/site/Seo";
import { PageHero } from "@/components/site/Layout";
import { Reveal } from "@/components/site/Reveal";
import { WhatsAppButton } from "@/components/site/WhatsButton";

const VALUES = [
  { icon: <Heart size={22} />, t: "Passione per il verde", d: "Curiamo ogni spazio come se fosse il nostro." },
  { icon: <Leaf size={22} />, t: "Soluzioni personalizzate", d: "Ogni intervento è studiato sulle reali esigenze del cliente." },
  { icon: <ShieldCheck size={22} />, t: "Serietà e precisione", d: "Lavoriamo con ordine, cura e rispetto degli spazi." },
  { icon: <Sparkles size={22} />, t: "Attenzione ai dettagli", d: "Valutiamo con attenzione ogni pianta e ogni area." },
];

export default function About() {
  const { settings } = useData();
  return (
    <>
      <Seo title="Chi siamo" description="Animaverde nasce dalla passione per la cura degli spazi verdi. Un servizio attento, ordinato e personalizzato per giardini di privati e aziende." path="/chi-siamo" />
      <PageHero
        overline="Chi siamo"
        title="Passione per il verde, cura in ogni intervento"
        breadcrumb={<><Link to="/" className="hover:text-av-lime">Home</Link> / <span className="text-av-white">Chi siamo</span></>}
      />

      <section className="bg-av-black py-20 sm:py-28">
        <div className="mx-auto max-w-[1400px] px-5 sm:px-8 grid lg:grid-cols-2 gap-14 items-center">
          <Reveal>
            <p className="font-display text-2xl sm:text-3xl leading-snug text-av-white">{settings?.about_testo}</p>
            <p className="mt-6 text-av-gray leading-relaxed">
              Non facciamo promesse che non possiamo mantenere: preferiamo la concretezza. Ogni lavoro parte da un ascolto attento
              e da un sopralluogo gratuito, per proporre soluzioni realistiche, durature e semplici da mantenere nel tempo.
            </p>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="relative overflow-hidden rounded-3xl aspect-[4/5]">
              <img src="https://images.unsplash.com/photo-1774579893308-ec777199ce32?crop=entropy&cs=srgb&fm=jpg&q=85&w=800" alt="Cura del verde" className="w-full h-full object-cover" loading="lazy" />
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-av-white text-av-black py-20 sm:py-28">
        <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
          <Reveal><h2 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight mb-14">I valori che guidano il nostro lavoro</h2></Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {VALUES.map((v, i) => (
              <Reveal key={v.t} delay={i * 0.08}>
                <div className="w-12 h-12 rounded-full flex items-center justify-center mb-5" style={{ backgroundColor: "var(--av-green-dark)", color: "var(--av-green-lime)" }}>{v.icon}</div>
                <h3 className="font-display text-xl">{v.t}</h3>
                <p className="mt-2 text-neutral-600 text-sm leading-relaxed">{v.d}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-av-green-dark py-16" style={{ backgroundColor: "var(--av-green-deep)" }}>
        <div className="mx-auto max-w-[1400px] px-5 sm:px-8 grid lg:grid-cols-2 gap-10 items-center">
          <Reveal>
            <div className="rounded-3xl border border-dashed border-white/25 aspect-[16/9] flex items-center justify-center text-center p-8">
              <p className="text-av-gray text-sm">Spazio riservato a una futura fotografia del titolare o della squadra.<br />(Segnaposto da sostituire)</p>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="font-display text-3xl sm:text-4xl text-av-white">Parla con Animaverde</h2>
            <p className="mt-4 text-av-gray leading-relaxed">Vuoi conoscerci meglio o raccontarci il tuo progetto? Siamo a disposizione per un primo confronto, senza impegno.</p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/contatti" className="inline-flex items-center gap-2 rounded-full bg-av-bright text-av-black px-6 py-3 text-sm font-semibold hover:bg-av-lime transition-colors">Richiedi un sopralluogo gratuito</Link>
              <WhatsAppButton variant="outline" />
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
